/// <amd-module name="Analytics/Shared/Visuals/DonutChart/Components/DonutChart" />
import * as React from "react";
import { Chart } from "VSS/Features/Charts/Chart";
import { ChartProps, ChartTypesConstants, ClickEvent, PieChartOptions } from "VSS/Features/Charts/Contracts";
import { Datum } from "VSS/Features/Charts/Contracts";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface IDonutChartProps extends IVssComponentProperties {
    title?: string;
    errorText?: string;
    chartDiameter?: number;
    enableLegends?: boolean;
    chartInnerSize?: number;
    chartWidth?: number;
    chartHeight?: number;
    contentRenderer?: (text: string) => JSX.Element;
    chartData?: Datum[];
    customizeTooltip?: (value: number) => string;
    onChartClick?: (clickEvent: ClickEvent) => void;
}

export class DonutChart extends VssComponent<IDonutChartProps, {}> {
    private static _getXLabels(chartProps: IDonutChartProps) {
        return chartProps.chartData ? chartProps.chartData.map(chartData => chartData.name || "") : [];
    }

    /**
     * Converts  component props to ChartOptions
     */
    public static formatChartOptions(chartProps: IDonutChartProps): ChartProps {
        const offset = 60;
        const diameter = chartProps.chartDiameter || 120;
        const legendCount = chartProps.chartData ? chartProps.chartData.length : 0;
        const enableLegends = chartProps.enableLegends != null ? chartProps.enableLegends : true;
        const formattedChartOptions = {
            title: chartProps.title,
            hostOptions: {
                width: chartProps.chartWidth || 320,
                height: chartProps.chartHeight || diameter + offset + (enableLegends ? Math.floor(legendCount / 3) * 20 : 0)
            },
            legend: {
                enabled: enableLegends,
                limitLabelSize: true,
                align: "center"
            },
            chartType: ChartTypesConstants.Pie,
            pageContext: chartProps.pageContext,
            suppressMargin: true,
            suppressAnimation: true,
            specializedOptions: {
                size: diameter.toString() + "px",
                innerSize: chartProps.chartInnerSize ? chartProps.chartInnerSize : "1px",
                showLabels: false,
                center: [null, diameter / 2]
            } as PieChartOptions,
            series: [
                {
                    data: chartProps && chartProps.chartData ? chartProps.chartData.filter(o => o.y !== 0) : []
                }
            ],
            xAxis: { labelValues: DonutChart._getXLabels(chartProps) }
        } as ChartProps;

        if (chartProps.customizeTooltip) {
            formattedChartOptions.tooltip = {
                customTooltipMapping: points => {
                    if (points && points.length > 0 && points[0].values && points[0].values.length > 0) {
                        const toolTipValue = chartProps.customizeTooltip!(points[0].values[0] as number);
                        return [
                            {
                                text: toolTipValue
                            }
                        ];
                    } else {
                        return [
                            {
                                text: ""
                            }
                        ];
                    }
                }
            };
        }

        if(chartProps.onChartClick){
            formattedChartOptions.click = chartProps.onChartClick;
        }

        return formattedChartOptions;
    }
    public render(): JSX.Element {
        if (this.props.errorText && this.props.contentRenderer) {
            return this.props.contentRenderer(this.props.errorText);
        }

        if (this.props.chartData) {
            return (
                <div className={this.props.className || "donut-chart-visual"}>
                    <Chart {...this._getDonutChartOptions()} className="flex-column" />
                </div>
            );
        } else {
            return this._loadingComponent();
        }
    }

    private _loadingComponent(): JSX.Element {
        return <Spinner className="card-spinner flex-column flex-grow" size={SpinnerSize.large} />;
    }

    private _getDonutChartOptions(): any {
        return DonutChart.formatChartOptions(this.props);
    }
}
